import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery } from '@apollo/client'
import { GET_USER_NAMES } from 'graphql/users/queries'

export function UserListDropdown({ userType, fields, purpose, isActive }) {
    const { t } = useTranslation()
    const [userList, setUserList] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    
    const [getUserList, { loading: userListLoading, error: userListError }] = useLazyQuery(GET_USER_NAMES, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    })

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true)
            setError(null)
            
            try {
                if (userType === 'captain') {
                    // For captain type, fetch both bh_captain and qa_captain roles
                    const captainRoles = ['bh_captain', 'qa_captain']
                    const allUsers = []
                    
                    for (const role of captainRoles) {
                        const result = await getUserList({
                            variables: {
                                userPool: 'admin',
                                role: role,
                                isActive: isActive ? isActive : null,
                                page: {
                                    limit: 100,
                                    offset: 0,
                                },
                            },
                        })
                        
                        if (result.data?.searchUser?.users) {
                            allUsers.push(...result.data.searchUser.users)
                        }
                    }
                    
                    // Remove duplicates based on user ID
                    const uniqueUsers = allUsers.filter((user, index, self) => 
                        index === self.findIndex(u => u.id === user.id)
                    )
                    
                    setUserList(uniqueUsers)
                } else if (userType === 'seaman') {
                    // For seaman type, fetch both bh_seaman and qa_seaman roles
                    const seamanRoles = ['bh_seaman', 'qa_seaman']
                    const allUsers = []
                    
                    for (const role of seamanRoles) {
                        const result = await getUserList({
                            variables: {
                                userPool: 'admin',
                                role: role,
                                isActive: isActive ? isActive : null,
                                page: {
                                    limit: 100,
                                    offset: 0,
                                },
                            },
                        })
                        
                        if (result.data?.searchUser?.users) {
                            allUsers.push(...result.data.searchUser.users)
                        }
                    }
                    
                    // Remove duplicates based on user ID
                    const uniqueUsers = allUsers.filter((user, index, self) => 
                        index === self.findIndex(u => u.id === user.id)
                    )
                    
                    setUserList(uniqueUsers)
                } else {
                    // For other user types, use the original logic
                    const result = await getUserList({
                        variables: {
                            userPool: 'admin',
                            role: userType,
                            isActive: isActive ? isActive : null,
                            page: {
                                limit: 100,
                                offset: 0,
                            },
                        },
                    })
                    
                    if (result.data?.searchUser?.users) {
                        setUserList(result.data.searchUser.users)
                    }
                }
            } catch (err) {
                setError(err)
            } finally {
                setLoading(false)
            }
        }
        
        fetchUsers()
    }, [userType, isActive])

    return (
        <>
            {loading && <div className="text-sm text-gray-500 mt-1">Loading ....</div>}
            {error && (
                <select>
                    <option value={null}>{t('No data found')}</option>
                </select>
            )}
            {!loading && !error && userList?.length === 0 && (
                <select>
                    <option value={null}>{t('No data found')}</option>
                </select>
            )}
            {!loading && !error && userList?.length > 0 && (
                <select
                    {...fields}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                >
                    {purpose === 'filter' ? (
                        <option value={null}> {t('All')} </option>
                    ) : (
                        <option key={'1'} value="" disabled>
                            {t('All')}
                        </option>
                    )}
                    {userList.map(function (user) {
                        return (
                            <option key={user.id} value={user.id}>
                                {user.user_info.given_name} {user.user_info.family_name}
                            </option>
                        )
                    })}
                </select>
            )}
        </>
    )
}
